import { useAppDispatch, useAppSelector } from '@shared/model';
import {
  selectEmailSendingProgress,
  selectIsSendingEmails,
  selectSenderErrors,
  setIsSendingEmail,
} from '@entities/sender-state/model';
import {
  selectIsSendingTimedOut,
  selectSendingStartedAt,
} from '@pages/invoice-generator/shared/model/reportsSection.selectors';
import { ReportListItem } from '@pages/invoice-generator/shared/ui/Report/components/ReportListItem';
import { FC } from 'react';
import { useElapsedTime } from '@pages/invoice-generator/shared/lib/hooks';
import { setSendingIsTimedOut } from '@pages/invoice-generator/shared/model';
import { Report } from '@pages/invoice-generator/shared/ui/Report/Report';

interface IEmailSendingReport {
  reportHeaderClassName?: string;
}

export const EmailSendingReport: FC<IEmailSendingReport> = ({ reportHeaderClassName }) => {
  const isSending = useAppSelector(selectIsSendingEmails);
  const sendingProgress = useAppSelector(selectEmailSendingProgress);
  const sendingErrors = useAppSelector(selectSenderErrors);
  const startedAt = useAppSelector(selectSendingStartedAt);
  const isTimedOut = useAppSelector(selectIsSendingTimedOut);
  const dispatch = useAppDispatch();

  const { elapsedTime, finishedAt } = useElapsedTime(isSending);
  const errors = sendingErrors.map(error => ({
    source: error.email,
    messages: [error.message],
  }));

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const retrySending = () => {
    dispatch(setIsSendingEmail(true));
    dispatch(setSendingIsTimedOut(false));
  };

  return (
    <Report
      reportTitle="Email Sending Report"
      isTimedOut={isTimedOut}
      onRetry={retrySending}
      headerClassName={reportHeaderClassName}
      data-id="email-sending-report"
      errors={errors}
    >
      <ReportListItem data-id="started-at-li" title="Started at" value={new Date(startedAt).toLocaleString()} />
      <ReportListItem
        data-id="finished-at-li"
        title="Finished at"
        value={isSending ? 'In progress' : new Date(finishedAt).toLocaleString()}
      />
      <ReportListItem data-id="duration-li" title="Duration" value={formatTime(elapsedTime)} />
      <ReportListItem data-id="total-li" title="Total" value={String(sendingProgress.totalSending)} />
      <ReportListItem data-id="sent-li" title="Sent" value={String(sendingProgress.totalSent)} />
      <ReportListItem data-id="delivered-li" title="Delivered" value={String(sendingProgress.totalDelivered)} />
      <ReportListItem
        data-id="did-not-react-recipient-li"
        title="Didn't reach recipient"
        value={String(sendingProgress.totalBounced + sendingProgress.totalComplaints)}
      />
    </Report>
  );
};
